import { useEffect, useMemo, useReducer } from 'preact/hooks';
import ResizeObserver from 'resize-observer-polyfill';
import { AlmostEqual } from '../../math';

type Size = { width: number; height: number };
const InitialState = { width: 0, height: 0 };
const ReducerFn = (s: Size, v: Size) => (AlmostEqual(s.width, v.width) && AlmostEqual(s.height, v.height) ? s : v);

export function useResizeObserver(target: HTMLElement | null): Size {
  const [size, setSize] = useReducer(ReducerFn, InitialState);

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((a) => {
        const [
          {
            contentRect: { width, height },
          },
        ] = a;
        // Note: This requestAnimationFrame prevents the "ResizeObserver loop limit exceeded" error
        setTimeout(() => {
          setSize({ width, height });
        }, 0);
      }),
    []
  );

  useEffect(() => {
    if (!target) {
      setSize(InitialState);
      return () => void 0;
    } else {
      const { width, height } = target.getBoundingClientRect();
      setSize({ width, height });

      resizeObserver.observe(target);
      return () => resizeObserver.disconnect();
    }
  }, [resizeObserver, target]);

  return size;
}
